import React from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StyledDialog } from 'theme/index';

export type PendingStep = 'PROFILE' | 'CONSENT' | 'MFSH' | 'MWISE';

interface DashboardNextStepDialogProps {
  open: boolean;
  pendingStep: PendingStep;
  onPrimaryClick: () => void;
  onClose: () => void;
}

const STEP_CONFIG: Record<
  PendingStep,
  {
    title: string;
    completedMessage?: string;
    nextStepMessage: string;
    primaryButton: string;
    image: string;
  }
> = {
  PROFILE: {
    //add apostrophe to "Let's Get Started!"  
    title: 'Let\'s Get Started!',
    nextStepMessage: 'Step 1 of 5 is completing your profile.',
    primaryButton: 'Complete Profile',
    image: '/images/dashwork4.png',
  },
  CONSENT: {
    title: 'Great!',
    completedMessage: 'Step 2 of 5 completed.',
    nextStepMessage: 'Step 3 is signing consent forms.',
    primaryButton: 'Sign Now',
    image: '/images/dashwork1.png',
  },
  MFSH: {
    title: 'Excellent!',
    completedMessage: 'Step 3 of 5 completed.',
    nextStepMessage: 'Step 4 is completing your Medical, Family & Social History.',
    primaryButton: 'Complete Now',
    image: '/images/Family_Icon.png',
  },
  MWISE: {
    title: 'Almost There!',
    completedMessage: 'Step 4 of 5 completed.',
    nextStepMessage: 'Final step: Complete the M-Wise™ Assessment.',
    primaryButton: 'Complete Now',
    image: '/images/Brain_icon.png',
  },
};

const DashboardNextStepDialog = ({
  open,
  pendingStep,
  onPrimaryClick,
  onClose,
}: DashboardNextStepDialogProps) => {
  const config = STEP_CONFIG[pendingStep];

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      aria-describedby='next-step-dialog'
    >
      <DialogContent sx={{ position: 'relative', pt: 4, px: 3 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            component='img'
            src={config.image}
            alt={pendingStep}
            sx={{
              width: { xs: 125, sm: 125 },
              height: 'auto',
              objectFit: 'contain',
              mb: 2,
            }}
          />

          <Typography variant='h1' gutterBottom>
            {config.title}
          </Typography>

          {config.completedMessage ? (
            <Typography variant='body1' gutterBottom>
              {config.completedMessage}
            </Typography>
          ) : null}

          <Typography variant='body1' gutterBottom>
            {config.nextStepMessage}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          px: 3,
          pb: 4,
        }}
      >
        <Button
          variant='outlined'
          fullWidth
          sx={{ maxWidth: { sm: 180 } }}
          onClick={onClose}
        >
          Later
        </Button>
        <Button
          fullWidth
          sx={{ maxWidth: { sm: 180 }, mt: { xs: 0, sm: 0 } }}
          onClick={onPrimaryClick}
        >
          {config.primaryButton}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DashboardNextStepDialog;
